import type { SectionPlanInfo } from '@az3fr/core';
import { Input, Textarea } from '@headlessui/react';
import { ChevronDownIcon } from 'lucide-react';
import { memo, useCallback, useEffect, useId, useRef, useState } from 'react';

import { Slider } from '@/components/ui/slider';
import { sectionPlanSliceSelectors } from '@/entities/section-plan/model/section-plan.selector';
import { workoutCreatorThunks } from '@/features/workout-creator/model';
import { AddSectionButtonV2 } from '@/features/workout-creator/ui/v2/add-section-button-v2';
import { SectionPlanEditorV2 } from '@/features/workout-creator/ui/v2/section-editor-v2';
import { useAppDispatch, useAppSelector } from '@/hooks/store.hook';
import { cn } from '@/lib/utils';

function WorkoutPlanCreatorV2() {
	const dispatch = useAppDispatch();
	const workoutCreator = useAppSelector((state) => state.workoutCreator);
	const { activePlanId } = workoutCreator;
	const initializationRef = useRef(false);
	const [isDetailsCollapsed, setIsDetailsCollapsed] = useState(false);

	// Generate unique IDs for form inputs
	const nameInputId = useId();
	const descriptionInputId = useId();

	// Get sections for the current workout plan
	const workoutSections = useAppSelector((state) =>
		sectionPlanSliceSelectors.selectAllByWorkoutPlanId(state, activePlanId),
	);

	// If no workout plan is active, start a new one
	useEffect(() => {
		// Protect against double-mounting in development
		if (!initializationRef.current && !activePlanId) {
			initializationRef.current = true;
			dispatch(workoutCreatorThunks.startNewWorkoutPlan({}));
		}
	}, [dispatch, activePlanId]);

	const updateWorkout = useCallback(
		(field: 'name' | 'description', value: string) => {
			if (!activePlanId) {
				return;
			}

			dispatch(
				workoutCreatorThunks.updateWorkoutPlanProperties({
					workoutPlanId: activePlanId,
					changes: { [field]: value },
				}),
			);
		},
		[dispatch, activePlanId],
	);

	const updateWorkoutName = useCallback(
		(e: React.FocusEvent<HTMLInputElement>) => {
			updateWorkout('name', e.target.value);
		},
		[updateWorkout],
	);

	const updateWorkoutDescription = useCallback(
		(e: React.FocusEvent<HTMLTextAreaElement>) => {
			updateWorkout('description', e.target.value);
		},
		[updateWorkout],
	);

	return (
		<div className="min-h-screen bg-gray-100 px-3 py-4 sm:px-4">
			<div className="mx-auto max-w-2xl">
				<div className="space-y-3 sm:space-y-4">
					{/* --- Workout Details --- */}
					<div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
						<button
							type="button"
							onClick={() => setIsDetailsCollapsed(!isDetailsCollapsed)}
							className={cn(
								'group relative flex w-full items-center justify-between border-gray-200 border-b bg-white px-4 py-4 text-left transition-colors hover:bg-gray-50/50 focus:outline-none sm:px-5',
								isDetailsCollapsed && 'border-b-0',
							)}
						>
							<div className="space-y-1">
								<h1 className="font-semibold text-base text-gray-900 leading-tight sm:text-lg">
									New Workout
								</h1>
								<p className="font-medium text-gray-500 text-xs uppercase tracking-widest">
									Workout details
								</p>
							</div>
							<ChevronDownIcon
								className={cn(
									'size-5 shrink-0 text-gray-400 transition-transform duration-200 sm:size-6',
									isDetailsCollapsed ? 'rotate-0' : 'rotate-180',
								)}
							/>
						</button>

						<Slider isOpen={!isDetailsCollapsed} transitionDuration={200}>
							<div className="space-y-3 p-3 sm:space-y-5 sm:p-5">
								<div>
									<label
										htmlFor={nameInputId}
										className="mb-1 block font-medium text-gray-700 text-xs uppercase tracking-widest sm:mb-2"
									>
										Name
										<span className="ml-1 text-rose-500">*</span>
									</label>
									<Input
										id={nameInputId}
										placeholder="Enter workout name..."
										className="h-8 w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 font-medium text-gray-900 text-sm transition-colors placeholder:text-gray-400 hover:border-gray-400 focus:border-gray-400 focus:outline-none sm:h-10 sm:px-3 sm:py-2.5"
										onBlur={updateWorkoutName}
									/>
								</div>

								<div>
									<label
										htmlFor={descriptionInputId}
										className="mb-1 block font-medium text-gray-700 text-xs uppercase tracking-widest sm:mb-2"
									>
										Description
										<span className="ml-1 font-normal text-gray-400 text-xs normal-case">
											(optional)
										</span>
									</label>
									<Textarea
										id={descriptionInputId}
										placeholder="Add notes about this workout..."
										rows={2}
										className="w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 font-medium text-gray-900 text-sm leading-relaxed transition-colors placeholder:text-gray-400 hover:border-gray-400 focus:border-gray-400 focus:outline-none sm:rounded-lg sm:px-3 sm:py-2.5"
										onBlur={updateWorkoutDescription}
									/>
								</div>
							</div>
						</Slider>
					</div>

					{/* --- Sections List --- */}
					<div className="space-y-3 sm:space-y-4">
						{workoutSections.map((section: SectionPlanInfo, index: number) => (
							<SectionPlanEditorV2 key={section.id} sectionPlanId={section.id} orderIndex={index} />
						))}
					</div>

					{/* --- Add Section Button --- */}
					{activePlanId && <AddSectionButtonV2 workoutPlanId={activePlanId} />}
				</div>

				{/* --- Save Button --- */}
				<div className="py-4 sm:py-6">
					<button
						className="w-full rounded-lg bg-gray-900 px-4 py-3 font-semibold text-sm text-white transition-colors hover:bg-gray-800 focus:outline-none active:bg-gray-950 sm:text-base"
						type="button"
					>
						Save Workout
					</button>
				</div>
			</div>
		</div>
	);
}

// Memoize the main component to prevent unnecessary rerenders
const MemoizedWorkoutPlanCreator = memo(WorkoutPlanCreatorV2);

export { MemoizedWorkoutPlanCreator as WorkoutPlanCreatorV2 };
